import store from "@/store/store.js";

export default class PersonaHelpers {
  /**
   * Gets persona property object (epids or ipids) based on selected personas.
   * @param {Array} selectedPersonas - Array of persona identifiers
   * @returns {Object} Object with epids or ipids property, or empty object if all personas selected
   */
  static getPersonaProperty(selectedPersonas) {
    const mapPersonas = store.state.CONTENT.mapPersonas;
    const isAllPersonasSelected = mapPersonas.every((persona) => selectedPersonas.includes(persona.personaIdentifier));
    if (isAllPersonasSelected) {
      return {};
    }
    const isDefaultPersonaSelected = selectedPersonas.includes(
      store.getters["CONTENT/defaultPersona"].personaIdentifier
    );
    if (isDefaultPersonaSelected) {
      return {
        epids: mapPersonas
          .filter((persona) => !selectedPersonas.includes(persona.personaIdentifier))
          .map((persona) => persona.personaIdentifier)
      };
    }
    return {
      ipids: selectedPersonas
    };
  }

  /**
   * Gets the selected personas array from a feature based on its properties.
   * @param {Object} feature - Feature object with properties
   * @returns {Array} Array of selected persona identifiers
   */
  static setPersonaSelection(feature) {
    let selectedPersonas = [];

    const mapPersonas = store.state.CONTENT.mapPersonas;
    const selectedPersona = store.state.CONTENT.selectedPersona;
    if (feature?.properties?.ipids) {
      selectedPersonas = [...feature.properties.ipids];
    } else if (feature?.properties?.epids) {
      selectedPersonas = [
        ...mapPersonas
          .map((persona) => persona.personaIdentifier)
          .filter((personaId) => !feature.properties.epids.includes(personaId))
      ];
    } else if (!feature && selectedPersona !== undefined && selectedPersona !== "all-personas") {
      selectedPersonas = [selectedPersona];
    } else {
      selectedPersonas = [...mapPersonas.map((persona) => persona.personaIdentifier)];
    }

    return selectedPersonas;
  }

  /**
   * Gets personas for a feature based on its properties.
   * @param {Object} feature - Feature object with properties
   * @returns {Array} Array of persona objects that match the feature
   */
  static getPersonasForFeature(feature) {
    const mapPersonas = store.state.CONTENT.mapPersonas;
    if (feature?.properties?.ipids) {
      return mapPersonas.filter((persona) => feature.properties.ipids.includes(persona.personaIdentifier));
    }
    if (feature?.properties?.epids) {
      return mapPersonas.filter((persona) => !feature.properties.epids.includes(persona.personaIdentifier));
    }
    return mapPersonas;
  }

  /**
   * Calculates selectedPersonaSet from selected items.
   * @param {Array} selectedItemIds - Array of selected item IDs
   * @param {Function} getFeatureById - Function to get feature by ID: (itemId) => feature
   * @returns {Map} Map with personaId as keys and count as values
   */
  static calculateSelectedPersonaSet(selectedItemIds, getFeatureById) {
    const selectedPersonaSet = new Map();
    selectedItemIds.forEach((itemId) => {
      const feature = getFeatureById(itemId);
      if (feature) {
        const personas = PersonaHelpers.getPersonasForFeature(feature);
        personas.forEach((persona) => {
          const currentCount = selectedPersonaSet.get(persona.personaIdentifier) || 0;
          selectedPersonaSet.set(persona.personaIdentifier, currentCount + 1);
        });
      }
    });
    return selectedPersonaSet;
  }

  /**
   * Handles persona selection logic.
   * @param {string} personaId - ID of the persona being selected
   * @param {Map} selectedPersonaSet - Current selectedPersonaSet Map (will be modified)
   * @param {number} selectedItemCount - Number of currently selected items
   * @param {Array} mapPersonas - Array of all available personas
   */
  static handlePersonaSelected(personaId, selectedPersonaSet, selectedItemCount, mapPersonas) {
    if (personaId === "all-personas") {
      selectedPersonaSet.clear();
      mapPersonas.forEach((persona) => {
        selectedPersonaSet.set(persona.personaIdentifier, selectedItemCount);
      });
    } else {
      selectedPersonaSet.set(personaId, selectedItemCount);
    }
  }

  /**
   * Handles persona removal logic.
   * @param {string} personaId - ID of the persona being removed
   * @param {Map} selectedPersonaSet - Current selectedPersonaSet Map (will be modified)
   */
  static handlePersonaRemoved(personaId, selectedPersonaSet) {
    if (personaId === "all-personas") {
      selectedPersonaSet.clear();
    } else {
      selectedPersonaSet.delete(personaId);
    }
  }

  /**
   * Builds a feature update request with persona assignments, preserving existing personas for indeterminate ones.
   * @param {Object} feature - Feature object to update
   * @param {Map} selectedPersonaSet - Map with personaId as keys and count as values
   * @param {number} selectedItemCount - Total number of selected items
   * @returns {Object} Feature request object with updated properties
   */
  static buildFeatureUpdateRequest(feature, selectedPersonaSet, selectedItemCount) {
    const properties = { ...feature.properties };
    delete properties.epids;
    delete properties.ipids;

    // Get common personas (personas that all selected items have)
    const commonPersonas = Array.from(selectedPersonaSet.entries())
      .filter(([, count]) => count === selectedItemCount)
      .map(([personaId]) => personaId);

    // Get existing personas for this feature and keep only those that are in selectedPersonaSet
    const existingPersonas = PersonaHelpers.getPersonasForFeature(feature).map((persona) => persona.personaIdentifier);
    const existingPersonasToKeep = existingPersonas.filter((persona) => selectedPersonaSet.has(persona));

    // Combine common personas with existing personas to keep (for indeterminate ones)
    const personasToApply = [...new Set([...existingPersonasToKeep, ...commonPersonas])];

    return {
      type: "Feature",
      properties: {
        ...properties,
        ...PersonaHelpers.getPersonaProperty(personasToApply)
      },
      geometry: feature.geometry
    };
  }
  /**
   * Caches the selected persona and sets it to "all-personas"
   */
  static cacheSelectedPersona() {
    const currentSelectedPersona = store.state.CONTENT.selectedPersona;
    const cachedSelectedPersona = store.state.CONTENT.cachedSelectedPersona;
    if (!cachedSelectedPersona) {
      store.commit("CONTENT/CACHED_SELECTED_PERSONA", currentSelectedPersona);
    }
    store.commit("CONTENT/SELECTED_PERSONA", "all-personas");
  }

  /**
   * Restores the selected persona from the cache if it exists
   * @param {string} currentRouteName - The name of the current route
   * @returns void
   */
  static restoreSelectedPersona(currentRouteName) {
    const disabledRouteNames = ["Beacons", "BeaconAdd", "BeaconEdit", "BuildingAdd", "BuildingEdit"];
    if (disabledRouteNames.includes(currentRouteName)) {
      return;
    }
    const cachedSelectedPersona = store.state.CONTENT.cachedSelectedPersona;
    if (cachedSelectedPersona) {
      store.commit("CONTENT/SELECTED_PERSONA", cachedSelectedPersona);
      store.commit("CONTENT/CACHED_SELECTED_PERSONA", undefined);
    }
  }
}
